import React, { useState, useEffect } from "react";
import { Card, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Drawer, Divider, IconButton, CircularProgress } from "@mui/material";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import AddUserModal from "./AddUserModal";
import ConfirmDialog from "components/Modal/ConfirmDialog";
import axiosInstance from "services/api/axiosInstance";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  tenant_admin: "Tenant Admin",
  billing_manager: "Billing Manager",
  auditor: "Auditor",
  readonly_viewer: "Read-Only Viewer",
};

const ROLES_DATA = [
  { role: "Super Admin", permissions: "Full read/write across all tenants and modules", count: 1, scope: "All tenants, all modules" },
  { role: "Tenant Admin", permissions: "Full control over assigned tenants", count: 2, scope: "Assigned tenants only" },
  { role: "Billing Manager", permissions: "Read/write access to subscription and cost modules", count: 1, scope: "Cost & Subscription modules" },
  { role: "Auditor", permissions: "Read-only across all modules including audit logs", count: 1, scope: "All modules (read-only)" },
  { role: "Read-Only Viewer", permissions: "Dashboard-only access with no write permissions", count: 1, scope: "Dashboard only" },
];

const AUDIT_LOG = [
  { timestamp: "10 Jul 2025, 11:02", user: "aniruddh@vivre.io", action: "tenant.created", target: "Pied Piper", details: "New tenant provisioning started", ip: "103.21.58.12" },
  { timestamp: "10 Jul 2025, 09:45", user: "system", action: "alert.triggered", target: "Initech LLC", details: "Payment failed - 3rd retry", ip: "—" },
  { timestamp: "9 Jul 2025, 16:30", user: "adikeshava@vivre.io", action: "config.updated", target: "Acme Corp", details: "Feature flags updated (sara, aiCompanion)", ip: "103.21.58.15" },
  { timestamp: "9 Jul 2025, 14:10", user: "manikanta@vivre.io", action: "subscription.upgraded", target: "Wayne Media", details: "Starter → Growth", ip: "103.21.58.20" },
  { timestamp: "8 Jul 2025, 10:00", user: "system", action: "tenant.suspended", target: "Initech LLC", details: "Auto-suspended due to payment failure", ip: "—" },
  { timestamp: "7 Jul 2025, 08:15", user: "aniruddh@vivre.io", action: "user.created", target: "Vedant Kajale", details: "New user added with Read-Only Viewer role", ip: "103.21.58.12" },
  { timestamp: "6 Jul 2025, 17:00", user: "adikeshava@vivre.io", action: "config.updated", target: "Globex Industries", details: "Rate limits updated for /api/v1/search", ip: "103.21.58.15" },
  { timestamp: "5 Jul 2025, 12:30", user: "system", action: "health.check", target: "Platform", details: "All services healthy", ip: "—" },
];

function Admin() {
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_CONSOLE_API}/console/v1/auth/getUsers`);
      setUsers(response.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAddUser = (userData) => {
    // Refresh user list after add/edit
    fetchUsers();
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setAddUserOpen(true);
  };

  const handleOpenAdd = () => {
    setSelectedUser(null);
    setAddUserOpen(true);
  };

  const handleCloseModal = () => {
    setAddUserOpen(false);
    setSelectedUser(null);
  };

  const handleDeleteUser = async (user) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await axiosInstance.delete(`${process.env.REACT_APP_CONSOLE_API}/console/v1/auth/deleteUser/${userToDelete._id}`);
      fetchUsers();
    } catch (err) {
      console.error("Failed to delete user:", err);
      alert(err.response?.data?.message || "Failed to delete user");
    } finally {
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={3}>
        {/* Page Header with RBAC & Audit buttons */}
        <MDBox mb={2.5}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox>
              <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
                <span style={{ color: "#5D6D7E", fontSize: 12 }}>Control Plane</span>
                <span style={{ color: "#BDC3C7" }}>›</span>
                <span style={{ color: "#1E3A5F", fontSize: 12, fontWeight: 600 }}>Admin</span>
              </MDBox>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1E3A5F" }}>Admin Panel</h1>
            </MDBox>
            {/* Right-end buttons */}
            <MDBox display="flex" gap={1.5}>
              {/* <button
                onClick={() => setDrawerOpen("rbac")}
                style={{
                  background: drawerOpen === "rbac" ? "linear-gradient(135deg, #00ACC4 0%, #2F36CB 100%)" : "linear-gradient(135deg, #00ACC4 0%, #2F36CB 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 22px",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  transition: "all 0.15s",
                  opacity: drawerOpen === "rbac" ? 1 : 0.85,
                }}
              >
                <Icon sx={{ fontSize: "18px !important", color: "#fff" }}>security</Icon>
                RBAC
              </button> */}
              <button
                onClick={() => setDrawerOpen("audit")}
                style={{
                  background: drawerOpen === "audit" ? "linear-gradient(135deg, #00ACC4 0%, #2F36CB 100%)" : "linear-gradient(135deg, #00ACC4 0%, #2F36CB 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 22px",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  transition: "all 0.15s",
                  opacity: drawerOpen === "audit" ? 1 : 0.85,
                }}
              >
                <Icon sx={{ fontSize: "18px !important", color: "#fff" }}>history</Icon>
                Audit Logs
              </button>
            </MDBox>
          </MDBox>
        </MDBox>

        {/* Users Table */}
        <Card sx={{ border: "1px solid #E8EDF2" }}>
          <MDBox p={3}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h5" fontWeight="bold" color="dark">
                User Management
              </MDTypography>
              <MDButton variant="gradient" color="info" size="small" onClick={handleOpenAdd}>
                <Icon>person_add</Icon>&nbsp;Add User
              </MDButton>
            </MDBox>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usersLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <MDTypography variant="body2" color="secondary">No users found</MDTypography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((u) => (
                      <TableRow key={u._id || u.email}>
                        <TableCell>
                          <MDTypography variant="button" fontWeight="medium">{u.name || `${u.firstName} ${u.lastName}`}</MDTypography>
                        </TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Chip label={ROLE_LABELS[u.role] || u.role} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <MDTypography variant="caption" color="text">
                            {u.createdAt ? new Date(u.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                          </MDTypography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleEditUser(u)}>
                            <Icon fontSize="small" color="info">edit</Icon>
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteUser(u)}>
                            <Icon fontSize="small" color="error">delete</Icon>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </Card>
      </MDBox>
      <Footer />

      {/* Add/Edit User Modal */}
      <AddUserModal
        open={addUserOpen}
        onClose={handleCloseModal}
        onSubmit={handleAddUser}
        user={selectedUser}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setUserToDelete(null); }}
        onConfirm={confirmDelete}
        title="Delete User"
        message={<>Are you sure you want to delete <strong>{userToDelete?.name || userToDelete?.email || "this user"}</strong>? This action cannot be undone.</>}
        confirmLabel="Delete"
        variant="danger"
      />

      {/* RBAC Slide-in Panel */}
      <Drawer
        anchor="right"
        open={drawerOpen === "rbac"}
        onClose={() => setDrawerOpen(null)}
        hideBackdrop
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "calc(100vw - 250px)" },
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
          },
        }}
      >
        <MDBox sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Panel Header */}
          <MDBox
            sx={{
              background: "linear-gradient(135deg, #00ACC4 0%, #2F36CB 100%)",
              px: 3,
              py: 2.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <MDBox>
              <MDTypography sx={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>RBAC Roles</MDTypography>
              <MDTypography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 12, mt: 0.3 }}>
                Role-based access control configuration
              </MDTypography>
            </MDBox>
            <button
              onClick={() => setDrawerOpen(null)}
              style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", cursor: "pointer", borderRadius: 6, padding: "6px 10px", fontSize: 16, fontWeight: 700 }}
            >
              ✕
            </button>
          </MDBox>

          {/* Panel Body */}
          <MDBox sx={{ flex: 1, overflow: "auto", p: 3, backgroundColor: "#fff" }}>
            {ROLES_DATA.map((r, idx) => (
              <MDBox
                key={r.role}
                sx={{
                  mb: 2,
                  p: 2,
                  border: "1px solid #E8EDF2",
                  borderRadius: "10px",
                  background: "#fff",
                }}
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <MDTypography sx={{ fontSize: 14, fontWeight: 700, color: "#1E3A5F" }}>
                    {r.role}
                  </MDTypography>
                  <Chip label={`${r.count} user${r.count > 1 ? "s" : ""}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                </MDBox>
                <MDTypography sx={{ fontSize: 12, color: "#5D6D7E", mb: 0.5 }}>
                  {r.permissions}
                </MDTypography>
                <MDTypography sx={{ fontSize: 11, color: "#ADB5BD" }}>
                  Scope: {r.scope}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>
        </MDBox>
      </Drawer>

      {/* Audit Logs Slide-in Panel */}
      <Drawer
        anchor="right"
        open={drawerOpen === "audit"}
        onClose={() => setDrawerOpen(null)}
        hideBackdrop
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "calc(100vw - 250px)" },
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
          },
        }}
      >
        <MDBox sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Panel Header */}
          <MDBox
            sx={{
              background: "linear-gradient(135deg, #00ACC4 0%, #2F36CB 100%)",
              px: 3,
              py: 2.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <MDBox>
              <MDTypography sx={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>Audit Logs</MDTypography>
              <MDTypography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 12, mt: 0.3 }}>
                Immutable record of all platform operations
              </MDTypography>
            </MDBox>
            <button
              onClick={() => setDrawerOpen(null)}
              style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", cursor: "pointer", borderRadius: 6, padding: "6px 10px", fontSize: 16, fontWeight: 700 }}
            >
              ✕
            </button>
          </MDBox>

          {/* Panel Body */}
          <MDBox sx={{ flex: 1, overflow: "auto", p: 0, backgroundColor: "#fff" }}>
            {AUDIT_LOG.map((entry, idx) => (
              <MDBox
                key={idx}
                sx={{
                  px: 3,
                  py: 2,
                  borderBottom: "1px solid #F0F3F6",
                  "&:hover": { background: "#F8FAFC" },
                }}
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={entry.action}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: 10,
                        fontWeight: 600,
                        borderColor: entry.action.includes("created") ? "#27AE60" :
                                     entry.action.includes("suspended") || entry.action.includes("triggered") ? "#E74C3C" :
                                     entry.action.includes("updated") || entry.action.includes("upgraded") ? "#00ACC4" : "#5D6D7E",
                        color: entry.action.includes("created") ? "#27AE60" :
                               entry.action.includes("suspended") || entry.action.includes("triggered") ? "#E74C3C" :
                               entry.action.includes("updated") || entry.action.includes("upgraded") ? "#00ACC4" : "#5D6D7E",
                      }}
                    />
                    <MDTypography sx={{ fontSize: 13, fontWeight: 600, color: "#1E3A5F" }}>
                      {entry.target}
                    </MDTypography>
                  </MDBox>
                  <MDTypography sx={{ fontSize: 11, color: "#ADB5BD", whiteSpace: "nowrap" }}>
                    {entry.timestamp}
                  </MDTypography>
                </MDBox>
                <MDTypography sx={{ fontSize: 12, color: "#5D6D7E", mb: 0.5 }}>
                  {entry.details}
                </MDTypography>
                <MDBox display="flex" gap={2}>
                  <MDTypography sx={{ fontSize: 11, color: "#ADB5BD" }}>
                    By: {entry.user}
                  </MDTypography>
                  <MDTypography sx={{ fontSize: 11, color: "#ADB5BD" }}>
                    IP: {entry.ip}
                  </MDTypography>
                </MDBox>
              </MDBox>
            ))}
          </MDBox>
        </MDBox>
      </Drawer>
    </DashboardLayout>
  );
}

export default Admin;
